<template>
  <div v-loading="Account.active.isLoading" class="project">
    <template v-if="Account.active.data.projects.length > 0">
      <div class="project-search">
        <el-form inline @submit.native.prevent="onSearch">
          <el-form-item>
            <el-select v-model="filterModel.order_by"
                       :placeholder="$t('Sort')"
                       value-key="value_key">
              <el-option v-for="(combination,key) in order_combinations"
                         :key="key"
                         :label="combination.label"
                         :value="combination">
                {{ combination.label }}
                <i :class="combination.icon_class" />
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item v-if="!showSearchBox">
            <el-button class="collapsed-search"
                       icon="mdi mdi-magnify"
                       @click="toggleSearchBox" />
          </el-form-item>
          <el-form-item
            class="search-box-item">
            <el-input v-if="showSearchBox"
                      v-model="filterModel.search"
                      class="search-box-item-input"
                      size="large"
                      :placeholder="$t('Search')"
                      clearable>
              <i slot="suffix" class="mdi mdi-magnify search-icon" />
            </el-input>
            <el-button v-if="showSearchBox"
                       class="search-box-item-close"
                       type="primary"
                       size="mini"
                       @click="toggleSearchBox">
              {{ $t('Cancel') }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
      <div class="project-container">
        <ListItem v-for="project in list"
                  :key="project.id"
                  :expandable="expandable"
                  :project="project" />
      </div>
    </template>
    <ProjectNotFound v-else />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { chain, get, deburr } from 'lodash';
import posthog from 'posthog-js';
import { version } from '../../../package.json';
import ListItem from './components/ListItem';
import ProjectNotFound from './components/ProjectNotFound';

export default {
  components: {
    ListItem,
    ProjectNotFound
  },
  data() {
    return {
      order_combinations: [
        {
          value_key: 0,
          label: 'Name A-Z',
          prop: 'display_name',
          order: 'asc'
        },
        {
          value_key: 1,
          label: 'Name Z-A',
          prop: 'display_name',
          order: 'desc'
        },
        {
          value_key: 2,
          label: 'Last Visit',
          prop: 'last_visit.started_at',
          order: 'asc',
          icon_class: 'mdi mdi-sort-ascending'
        },
        {
          value_key: 3,
          label: 'Last Visit',
          prop: 'last_visit.started_at',
          order: 'desc',
          icon_class: 'mdi mdi-sort-descending'
        }
      ],
      filterModel: {
        order_by: '',
        search: ''
      },
      showSearchBox: true,
      expandable: true
    };
  },
  computed: {
    ...mapState(['Account']),
    list() {
      const { order_by, search } = this.filterModel;

      return chain(this.Account.active.data.projects)
        .filter(project => deburr(project.display_name).toLowerCase().includes(deburr(search).toLowerCase()))
        .orderBy(project => deburr(get(project, order_by.prop)) || false, order_by.order)
        .value();
    }
  },
  beforeCreate() {
    if (!document.getElementById('usetifulScript')) {
      // eslint-disable-next-line func-names
      (function (w, d, s) {
        const a = d.getElementsByTagName('head')[0];
        const r = d.createElement('script');
        r.async = 1;
        r.src = s;
        r.setAttribute('id', 'usetifulScript');
        r.dataset.token = 'c4ddfa98cbee832ddce511f0c3a4c40e';
        a.appendChild(r);
      }(window, document, 'https://www.usetiful.com/dist/usetiful.js'));
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const { projects } = vm.Account.active.data;

      // eslint-disable-next-line no-param-reassign
      if (projects.length <= 1) vm.expandable = false;

      if (from.name === 'login' && projects.length === 1) {
        vm.goToDefaultProject(projects[0]);
      }

      vm.displaySearchBox();
    });
  },
  created() {
    if (!window.location.href.includes('localhost')) {
      posthog.unregister('project_id');
      posthog.unregister(version);
    }
  },
  methods: {
    toggleSearchBox() {
      this.showSearchBox = !this.showSearchBox;
    },
    displaySearchBox() {
      if (document.body.clientWidth < 1025) {
        this.toggleSearchBox();
      }
    },
    goToDefaultProject(project) {
      const { Breadcrumbs } = this;
      const { default_dashboard } = project.settings;
      let route = {
        name: Breadcrumbs.projectDetail,
        params: {
          projectId: project.id
        }
      };

      if (default_dashboard) {
        route = {
          name: Breadcrumbs[`${default_dashboard}`],
          params: {
            projectId: project.id
          }
        };
      }

      this.$router.push(route);
    }
  }
};
</script>

<style lang="scss">
$paddingH: 100px;
$paddingV: 10px;

.project {
  display: flex;
  flex: 1;
  flex-direction: column;

  &-search {
    display: flex;
    justify-content: flex-end;
    padding: $paddingV $paddingH;
    background-color: $lynxWhite;

    .el-form {
      display: flex;
      align-items: center;

      &-item__content {
        display: flex;
      }

      .el-select {
        .el-input {
          font-size: 18px;
          font-weight: bold;

          &__inner {
            padding: 0;
            color: $dimGrey;
            background-color: $lynxWhite;
            border: none;
          }
        }
      }

      .collapsed-search {
        font-size: 22px;
        color: $dimGrey;
        border-left: 2px solid $baiSeWhite;

        &:hover {
          color: $dimGrey;
          background-color: $lynxWhite;
        }
      }

      .search-box-item {
        display: flex;
        flex-direction: row;

        &-input {
          border-left: 2px solid $baiSeWhite;

          .search-icon {
            &::before {
              padding: 5px 0;
            }
          }

          .el-input__inner {
            font-size: 18px;
            background-color: $lynxWhite;
            border: none;
          }
        }

        &-close {
          display: none;
          align-self: center;
          font-size: 12px;
        }
      }
    }

    .el-form-item {
      margin-bottom: 0;
    }

    .collapsed-search {
      background: transparent;
      border: 0;
    }

    @include breakpoint(lg-max) {
      box-sizing: border-box;
      justify-content: flex-end;
      padding: $paddingV 0;

      .el-form {
        .el-select {
          .el-input {
            font-size: 16px !important;
          }
        }

        .collapsed-search {
          font-size: 20px !important;
        }

        .search-box-item {
          &-input {
            .search-icon {
              display: none;
            }

            .el-input__inner {
              font-size: 16px !important;
            }
          }

          &-close {
            display: inline-block;
          }
        }
      }
    }
  }

  &-container {
    padding: $paddingV + 10 $paddingH;

    @include breakpoint(lg-max) {
      padding: 0 20px;
      margin-top: 30px;
    }
  }
}
</style>
